import Worker from "./worker";
import Log from "./logger";

import templateFileData from "./assets/template.html";

import {
    DATA_ATTR_AUTH_IMAGE,
    DATA_ATTR_AUTH_URL,
    DATA_ATTR_AUTO_START,
    DATA_ATTR_ID,
    DATA_ATTR_KEY,
    STATE_HALTED,
    STATE_PROCESSING,
    STATE_RUNNING,
    MESSAGE_TYPE_DEBUG,
    MESSAGE_TYPE_ERROR,
    MESSAGE_TYPE_INFO,
    MESSAGE_TYPE_WARNING,
    STATE_SUCCESS,
    STATE_ERROR,
    DATA_ATTR_API,
    DATA_ATTR_AUTH_HEADER,
    DATA_ATTR_AUTH_TYPE,
    DATA_ATTR_STYLE, DATA_ATTR_REUSE_CHALLENGE
} from "./constants";

class Auth {

    static instancePool = {};
    instanceId = null;

    element = null;
    options = {};

    isTouchScreen = false;

    loaderEl = null;
    dialogEl = null;
    messageEl = null;
    buttonEl = null;
    deepLinkEl = null;

    worker = null;
    started = false;

    constructor(options = {}) {

        // Make sure this default is always available in the constructor options
        options.element = options.element || "almefy-login";

        // Assign instance id and try to get the DOM element
        this.instanceId = Auth.generateInstanceId();
        this.element = typeof options.element === "string" ? document.getElementById(options.element) : options.element;

        // Check element, if it exists
        if (null === this.element) {
            this.log("Could not find element%s.".replace("%s", typeof options.element === "string" ? " \"%s\"".replace("%s", options.element) : ""), MESSAGE_TYPE_ERROR);
            return;
        }

        // Check if there is already an instance for this element available, and return it
        if (this.element.hasAttribute(DATA_ATTR_ID) && this.element.getAttribute(DATA_ATTR_ID) in Auth.instancePool) {
            this.log("This element was already initialized by \"%s\".".replace("%s", this.element.getAttribute(DATA_ATTR_ID)), MESSAGE_TYPE_WARNING);
            return Auth.instancePool[this.element.getAttribute(DATA_ATTR_ID)];

        } else {
            this.element.setAttribute(DATA_ATTR_ID, this.instanceId);
            Auth.instancePool[this.instanceId] = this;
        }

        // Default options
        const defaultOptions = {
            api: _DEFAULT_API,
            key: null,
            authUrl: null,
            authType: "jwt",
            authImage: "qrcode-standard",
            style: "boxed",
            autoStart: true,
            authHeader: "X-Almefy-Auth",
            showInstanceId: _IS_DEVELOPMENT
        };

        // Options provided by element's data attributes
        const attributeOptions = {};

        if (this.element.hasAttribute(DATA_ATTR_API))
            attributeOptions['api'] = this.element.getAttribute(DATA_ATTR_API);

        if (this.element.hasAttribute(DATA_ATTR_KEY))
            attributeOptions['key'] = this.element.getAttribute(DATA_ATTR_KEY);

        if (this.element.hasAttribute(DATA_ATTR_AUTH_URL))
            attributeOptions['authUrl'] = this.element.getAttribute(DATA_ATTR_AUTH_URL);

        if (this.element.hasAttribute(DATA_ATTR_AUTH_TYPE))
            attributeOptions['authType'] = this.element.getAttribute(DATA_ATTR_AUTH_TYPE);

        if (this.element.hasAttribute(DATA_ATTR_AUTH_IMAGE))
            attributeOptions['authImage'] = this.element.getAttribute(DATA_ATTR_AUTH_IMAGE);

        if (this.element.hasAttribute(DATA_ATTR_STYLE))
            attributeOptions['style'] = this.element.getAttribute(DATA_ATTR_STYLE);

        if (this.element.hasAttribute(DATA_ATTR_AUTO_START))
            attributeOptions['autoStart'] = this.element.getAttribute(DATA_ATTR_AUTO_START) !== "false";

        if (this.element.hasAttribute(DATA_ATTR_AUTH_HEADER))
            attributeOptions['authHeader'] = this.element.getAttribute(DATA_ATTR_AUTH_HEADER);

        // Merge all options, with constructor options having the highest priority
        this.options = { ...defaultOptions, ...attributeOptions, ...options };

        // Check settings
        let optionsValidated = true;

        if (!this.options.key) {
            this.log("Missing required \"key\" parameter.", MESSAGE_TYPE_ERROR);
            optionsValidated = false;
        }

        this.isTouchScreen = window.matchMedia("(pointer: coarse)").matches;

        this.injectTemplate();

        this.buttonEl.addEventListener("click", this.handleMessageButtonClick.bind(this));
        this.deepLinkEl.addEventListener("click", this.handleDeepLinkClick.bind(this));

        if (!optionsValidated)
            return;

        // Create or share a Worker for this instance
        this.worker = new Worker(this.getKey());

        if (this.options.autoStart)
            this.start();
    }

    getInstanceId() {
        return this.instanceId;
    }

    getKey() {
        return this.options.key;
    }

    getApi() {
        return this.options.api;
    }

    getAuthUrl() {
        return this.options.authUrl;
    }

    getAuthType() {
        return this.options.authType;
    }

    getAuthHeader() {
        return this.options.authHeader;
    }

    injectTemplate() {
        let fragment = (document.createRange(). createContextualFragment(templateFileData));

        fragment.firstChild.classList.add(this.options.style + "-style");

        this.loaderEl = fragment.querySelector(".almefy-loader");
        this.dialogEl = fragment.querySelector(".almefy-dialog");
        this.messageEl = fragment.querySelector(".almefy-message");
        this.buttonEl = fragment.querySelector(".almefy-button");
        this.deepLinkEl = fragment.querySelector(".almefy-deep-link");

        if (this.options.style === "pure") {
            let almefyLinkEl = fragment.querySelector(".almefy-link");
            almefyLinkEl.parentNode.removeChild(almefyLinkEl);

            let almefyClaimEl = fragment.querySelector(".almefy-claim");
            almefyClaimEl.parentNode.removeChild(almefyClaimEl);
        }

        if (this.options.showInstanceId) {
            fragment.querySelector(".almefy-instance-id").innerHTML = "Instance: %s".replace("%s", this.instanceId);
        } else {
            let authInstanceIdEl = fragment.querySelector(".almefy-instance-id");
            authInstanceIdEl.parentNode.removeChild(authInstanceIdEl);
        }

        this.element.prepend(fragment);
    }

    start() {
        if (this.started) {
            this.debug("Already started.");
            return;
        }

        this.debug("Starting instance.");

        this.started = true;
        this.worker.start(this.getInstanceId());

        return this;
    }

    stop() {
        if (!this.started) {
            this.debug("Already stopped.");
            this.showMessage(false);

            return;
        }

        this.debug("Stopping instance.");

        this.started = false;
        this.worker.stop(this.getInstanceId());
        this.showMessage(false);

        return this;
    }

    handleWorkerEvents(instance, event, data) {

        // Only handle events related to that instance
        if (instance !== this.instanceId) return;

        switch(event) {
            case STATE_HALTED:
                if (this.started === false) {
                    this.showProgress(false);
                    this.showMessage(false);
                    this.deepLinkEl.style.backgroundImage = null;

                    if (this.isTouchScreen) this.disableDeepLink();
                }

                break;

            case STATE_PROCESSING:
                if (this.started) {
                    this.deepLinkEl.style.backgroundImage = null;
                    this.showProgress()
                    this.showMessage(false);
                }

                break;

            case STATE_RUNNING:
                if (this.started) {
                    this.showProgress(false)
                    this.showMessage(false);

                    // Check, if image data is available in the response, otherwise use image URLs
                    if (data.authImagesData && data.authImagesData[this.options.authImage]) {
                        this.deepLinkEl.style.backgroundImage = "url(data:image/png;base64," + data.authImagesData[this.options.authImage] + ")";
                    } else if (data.authImages[this.options.authImage]) {
                        this.deepLinkEl.style.backgroundImage = "url(" + data.authImages[this.options.authImage] + ")";
                    } else {
                        this.log("Requested auth image type \"%s\" is not available.".replace("%s", this.options.authImage), MESSAGE_TYPE_ERROR);
                    }

                    if (this.isTouchScreen) this.enableDeepLink(data.id);
                }

                break;

            case STATE_SUCCESS:
                this.started = false;
                this.deepLinkEl.style.backgroundImage = null;

                this.showProgress(false)
                this.showMessage(false);

                this.element.dispatchEvent(new CustomEvent("almefy.auth.token", {
                    detail: {
                        token: data
                    }
                }));

                break;

            case STATE_ERROR:
                this.started = false;
                this.worker.stop(this.getInstanceId());

                this.deepLinkEl.style.backgroundImage = null;

                let messageType = MESSAGE_TYPE_ERROR;
                let message = "Could not initiate Almefy.";

                if (data && data.status) {
                    switch(data.status) {
                        case 400:
                            message = "Could not initiate Almefy. Check response."
                            break;

                        case 403:
                            message = "Website does not support Almefy yet."
                            break;

                        case 404:
                        case 410:
                            messageType = MESSAGE_TYPE_INFO;
                            message = "Login expired, please restart."
                            break;

                        default:
                            message = "Could not initiate Almefy."
                    }
                }

                this.showProgress(false)
                this.showMessage(message, messageType);

                this.log(message, messageType);

                break;
        }
    }

    enableDeepLink(challenge) {
        this.deepLinkEl.classList.add("active");
        this.deepLinkEl.setAttribute("href", "https://app.almefy.com/?c=" + challenge);
    }

    disableDeepLink() {
        this.deepLinkEl.classList.remove("active");
        this.deepLinkEl.setAttribute("href", "https://almefy.com");
    }

    handleDeepLinkClick(e) {
        if (!this.started || !this.isTouchScreen) {
            e.preventDefault();
        }
    }

    handleMessageButtonClick(e) {
        e.preventDefault();

        if (!this.started) {
            this.start();
        }
    }

    showProgress(show = true) {
        this.loaderEl.style.display = show === false ? "none" : "block";
    }

    showMessage(message, type = MESSAGE_TYPE_INFO) {

        this.dialogEl.classList.remove("type-log", "type-warning", "type-error", "type-success");

        if (message === false) {
            this.dialogEl.style.display = "none";
            this.messageEl.innerHTML = "";
            return;
        }

        this.dialogEl.classList.add("type-" + type);
        this.messageEl.innerHTML = message;
        this.dialogEl.style.display = "block";
    }

    debug(message) {
        return this.log(message, MESSAGE_TYPE_DEBUG);
    }

    log(message, type = MESSAGE_TYPE_INFO) {
        const entry = "[Almefy][Auth]%s ".replace("%s", this.instanceId ? "[%s]".replace("%s", this.instanceId) : "") + message;

        this.element.dispatchEvent(new CustomEvent("almefy.auth.log", {
            detail: {
                message: entry,
                type: type
            }
        }));

        Log.add(entry, type);

        return this;
    }

    static generateInstanceId() {
        return "almefy-" + (Object.keys(Auth.instancePool).length + 1);
    }

    /**
     * Return Auth instance by HTML element id, HTML element or instance id.
     *
     * @param i
     * @returns {null|*}
     */
    static getInstance(i) {
        if (typeof i === "string" && i in Auth.instancePool) {
            return Auth.instancePool[i];
        }

        let el = typeof i === "string" ? document.getElementById(i) : i;

        if (el && el.hasAttribute(DATA_ATTR_ID) && el.getAttribute(DATA_ATTR_ID) in Auth.instancePool) {
            return Auth.instancePool[el.getAttribute(DATA_ATTR_ID)];
        }

        return null;
    }
}

export default Auth;